import { observer } from 'mobx-react-lite'
import { Box, Group, Stack } from '@mantine/core'
import { AppBadge, AppSelect, AppSkeleton, AppText } from '../../app/common/ui'
import { DatePickerInput } from '@mantine/dates'
import { useStore } from '../../app/stores/store'
import { EmptyState } from '../../app/common/ui'
import type { FlagDto } from '../../app/models/flag'
import { FlagTargetType } from '../../app/models/flag'
import { formatDistanceToNow } from 'date-fns'

const TARGET_TYPE_LABEL: Record<number, string> = {
  [FlagTargetType.Post]: 'Post',
  [FlagTargetType.Comment]: 'Comment',
  [FlagTargetType.Part]: 'Part',
  [FlagTargetType.FirearmModel]: 'Firearm model',
  [FlagTargetType.Build]: 'Build',
  [FlagTargetType.PostComment]: 'Post comment',
  [FlagTargetType.SlopPost]: 'Slop post'
}

const TARGET_TYPE_COLOR: Record<number, string> = {
  [FlagTargetType.Post]: 'blue',
  [FlagTargetType.Comment]: 'orange',
  [FlagTargetType.Part]: 'green',
  [FlagTargetType.FirearmModel]: 'navy',
  [FlagTargetType.Build]: 'gray',
  [FlagTargetType.PostComment]: 'grape',
  [FlagTargetType.SlopPost]: 'pink'
}

function truncate (s: string, len: number) {
  if (!s) return '—'
  return s.length <= len ? s : s.slice(0, len) + '…'
}

export default observer(function FlagList () {
  const { moderationStore } = useStore()
  const { groupedFlags, loading, selectedFlag, filters, setFilter } = moderationStore

  return (
    <Box>
      <Group spacing="sm" mb="md" align="flex-end">
        <AppSelect
          label="Status"
          data={[
            { value: '0', label: 'Open' },
            { value: '1', label: 'Resolved' },
            { value: '2', label: 'Dismissed' },
            { value: '', label: 'All' }
          ]}
          value={String(filters.status ?? '')}
          onChange={(v) => setFilter('status', v === '' ? '' : parseInt(v ?? '0', 10))}
          size="sm"
          sx={{ width: 140 }}
        />
        <AppSelect
          label="Content type"
          data={[
            { value: '', label: 'All' },
            { value: 'Post', label: 'Post' },
            { value: 'Comment', label: 'Comment' },
            { value: 'Part', label: 'Part' },
            { value: 'FirearmModel', label: 'Firearm model' },
            { value: 'Build', label: 'Build' }
          ]}
          value={filters.targetType ?? ''}
          onChange={(v) => setFilter('targetType', v ?? '')}
          size="sm"
          sx={{ width: 160 }}
        />
        <DatePickerInput
          label="From"
          placeholder="From date"
          value={filters.dateFrom ? new Date(filters.dateFrom) : null}
          onChange={(d) => setFilter('dateFrom', d ? d.toISOString().slice(0, 10) : undefined)}
          size="sm"
          clearable
          sx={{ width: 140 }}
        />
        <DatePickerInput
          label="To"
          placeholder="To date"
          value={filters.dateTo ? new Date(filters.dateTo) : null}
          onChange={(d) => setFilter('dateTo', d ? d.toISOString().slice(0, 10) : undefined)}
          size="sm"
          clearable
          sx={{ width: 140 }}
        />
      </Group>

      {loading
        ? (
        <Stack spacing="xs">
          {[1, 2, 3, 4, 5].map((i) => (
            <AppSkeleton key={i} height={72} radius="md" />
          ))}
        </Stack>
          )
        : groupedFlags.length === 0
          ? (
        <EmptyState
          title="No flags found"
          message="No flags match the current filters."
        />
            )
          : (
        <Stack spacing="xs">
          {groupedFlags.map((flag: FlagDto) => {
            const isSelected = selectedFlag?.id === flag.id
            return (
              <Box
                key={flag.id}
                onClick={() => moderationStore.selectFlag(flag)}
                sx={{
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid var(--mantine-color-gray-3)',
                  backgroundColor: isSelected ? 'var(--mantine-color-blue-0)' : 'var(--mantine-color-gray-0)',
                  cursor: 'pointer'
                }}
              >
                <Group position="apart" noWrap>
                  <AppBadge size="sm" color={TARGET_TYPE_COLOR[flag.targetType] ?? 'gray'}>
                    {TARGET_TYPE_LABEL[flag.targetType] ?? 'Unknown'}
                  </AppBadge>
                  {flag.groupCount > 1 && (
                    <AppBadge size="sm" color="red">
                      {flag.groupCount} reports
                    </AppBadge>
                  )}
                </Group>
                <AppText size="sm" weight={500} mt={4} lineClamp={1}>
                  {truncate(flag.targetTitle, 45)}
                </AppText>
                <AppText size="xs" color="dimmed" mt={4}>
                  Reported by @{flag.reporterUsername} · {formatDistanceToNow(new Date(flag.createdAt), { addSuffix: true })}
                </AppText>
              </Box>
            )
          })}
        </Stack>
            )}
    </Box>
  )
})
